import { createContext, useContext, useEffect, useMemo, useCallback, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { TRANSLATIONS } from "./translations";
import { api } from "@/lib/api";
import { deepMerge } from "@/lib/pageContent";

const SUPPORTED = ["fr", "en"];
const DEFAULT_LANG = "fr";

const I18nContext = createContext({
  lang: DEFAULT_LANG,
  t: (k) => k,
  setLang: () => {},
  localePath: (p) => p,
  alternateUrls: () => ({}),
});

/**
 * Determine current language from the pathname.
 * /en or /en/... → "en"; otherwise → "fr".
 */
export function detectLangFromPath(pathname) {
  if (!pathname) return DEFAULT_LANG;
  const seg = pathname.split("/").filter(Boolean)[0];
  return SUPPORTED.includes(seg) ? seg : DEFAULT_LANG;
}

/** Resolve a translation key like "home.hero.title1" against a dict, with fallback to FR. */
function lookup(dict, key) {
  const path = key.split(".");
  let cur = dict;
  for (const p of path) {
    if (cur && typeof cur === "object" && p in cur) cur = cur[p];
    else return undefined;
  }
  return cur;
}

export function I18nProvider({ children }) {
  const location = useLocation();
  const navigate = useNavigate();
  const lang = detectLangFromPath(location.pathname);

  // Sync <html lang>
  useEffect(() => {
    document.documentElement.lang = lang === "en" ? "en" : "fr";
  }, [lang]);

  // Texts edited in the back-office override the bundled translations.
  const [overrides, setOverrides] = useState({});

  useEffect(() => {
    let alive = true;
    api
      .get(`/site-texts?lang=${lang}`)
      .then((r) => {
        if (alive && r.data && Object.keys(r.data).length) setOverrides(r.data);
      })
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, [lang]);

  const dict = useMemo(() => deepMerge(TRANSLATIONS[lang], overrides), [lang, overrides]);

  const t = useCallback(
    (key, fallback) => {
      let val = lookup(dict, key);
      if (val === undefined) val = lookup(TRANSLATIONS[DEFAULT_LANG], key);
      if (val === undefined) return fallback ?? key;
      return val;
    },
    [dict]
  );

  /** Prefix a path with the current locale (e.g. "/menu" → "/en/menu" if EN). */
  const localePath = useCallback(
    (path) => {
      if (lang === DEFAULT_LANG) return path || "/";
      if (!path || path === "/") return `/${lang}`;
      if (path.startsWith("/")) return `/${lang}${path}`;
      return `/${lang}/${path}`;
    },
    [lang]
  );

  /** Switch language while preserving the current path. */
  const setLang = useCallback(
    (next) => {
      if (!SUPPORTED.includes(next)) return;
      const segs = location.pathname.split("/").filter(Boolean);
      // Strip current lang prefix if any
      if (SUPPORTED.includes(segs[0])) segs.shift();
      const newPath = next === DEFAULT_LANG ? `/${segs.join("/")}` : `/${next}/${segs.join("/")}`;
      const clean = newPath.replace(/\/+$/, "") || "/";
      navigate(clean + location.search + location.hash);
      try { localStorage.setItem("entracte_lang", next); } catch (_) {}
    },
    [location.pathname, location.search, location.hash, navigate]
  );

  /** Compute hreflang alternate URLs for the current page. */
  const alternateUrls = useCallback(() => {
    if (typeof window === "undefined") return {};
    const segs = location.pathname.split("/").filter(Boolean);
    if (SUPPORTED.includes(segs[0])) segs.shift();
    const tail = segs.join("/");
    const origin = window.location.origin;
    return {
      fr: `${origin}/${tail}`.replace(/\/+$/, "") || `${origin}/`,
      en: `${origin}/en${tail ? `/${tail}` : ""}`,
      xDefault: `${origin}/${tail}`.replace(/\/+$/, "") || `${origin}/`,
    };
  }, [location.pathname]);

  const value = useMemo(
    () => ({ lang, t, setLang, localePath, alternateUrls }),
    [lang, t, setLang, localePath, alternateUrls]
  );

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n() {
  return useContext(I18nContext);
}

export function useT() {
  return useI18n().t;
}

export function useLang() {
  return useI18n().lang;
}

export function useLocalePath() {
  return useI18n().localePath;
}
